/**
 * Raffle projection screen
 * Full-screen display for projectors/large screens during live events
 */

import { useParams } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { haptic } from "@/lib/telegram";
import { useRaffleSocket } from "./hooks/useRaffleSocket";
import { TicketRoller } from "./components/TicketRoller";
import { PrizeDisplay } from "./components/PrizeDisplay";
import { WinnerAnnouncement } from "./components/WinnerAnnouncement";
import { Confetti } from "./components/Confetti";
import { ReactionBurst } from "./components/ReactionBurst";
import { RaffleLogo } from "./components/RaffleLogo";
import styles from "./RaffleScreen.module.css";
import "./raffle-theme.css";

// =============================================================================
// Component
// =============================================================================

export function RaffleScreen() {
  const { raffleId } = useParams<{ raffleId: string }>();
  const actualRaffleId = raffleId ?? "";

  // Refs for logo position calculation
  const logoRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update CSS variables for rays position based on actual logo DOM position
  const updateRaysPosition = useCallback(() => {
    if (!logoRef.current || !containerRef.current) return;
    
    const rect = logoRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    containerRef.current.style.setProperty('--logo-center-x', `${centerX}px`);
    containerRef.current.style.setProperty('--logo-center-y', `${centerY}px`);
  }, []);

  // Update position on mount, resize, and orientation change
  useEffect(() => {
    // Initial update after render
    const timer = setTimeout(updateRaysPosition, 50);
    
    // Update on resize/orientation
    window.addEventListener('resize', updateRaysPosition);
    window.addEventListener('orientationchange', updateRaysPosition);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateRaysPosition);
      window.removeEventListener('orientationchange', updateRaysPosition);
    };
  }, [updateRaysPosition]);

  // Hide global rays (from index.css) on this page - we have our own dark-themed rays
  useEffect(() => {
    document.documentElement.setAttribute('data-raffle-page', 'true');
    return () => {
      document.documentElement.removeAttribute('data-raffle-page');
    };
  }, []);

  // Transition animation state
  const [isTransitioning, setIsTransitioning] = useState(false);

  // WebSocket connection
  const {
    isConnected,
    isJoined,
    error,
    raffle,
    currentPrize,
    prizes,
    ticketCount,
    completedDraws,
    isDrawing,
    drawingPrize,
    currentTicketNumber,
    drawProgress,
    ticketNumbers,
    lastWinner,
    lastDrawnPrize,
    showingWinner,
    showWinnerConfirmed, // Admin-controlled winner display
    isCompleted,
    showCompletedConfirmed, // Admin-controlled final screen display
    reactionBurst,
    reactionTrigger,
    confirmAnimationComplete,
  } = useRaffleSocket({
    raffleId: actualRaffleId,
    onShowWinner: () => {
      // Strong vibration when winner is shown on broadcast
      haptic("success");
      
      // Vibration pattern for PWA/browser
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200, 100, 400, 100, 600]);
      }
      
      // Repeat haptic
      setTimeout(() => haptic("heavy"), 300);
      setTimeout(() => haptic("heavy"), 600);
      setTimeout(() => haptic("success"), 900);
    },
    onNextPrize: () => {
      // Trigger transition animation
      setIsTransitioning(true);
      // Reset after animation completes
      setTimeout(() => setIsTransitioning(false), 800);
    },
  });

  // Use showWinnerConfirmed for displaying winner screen (admin-controlled)
  const showWinner = showWinnerConfirmed;

  // Calculate prize counts for display
  const drawnCount = completedDraws.length;
  const totalPrizes = prizes.length;

  // Connection status display - using CSS-based icons instead of emoji
  const renderStatus = () => {
    if (!isConnected) {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusIconAnimated}>
            <span className={styles.pulseRing} />
            <span className={styles.pulseDot} />
          </div>
          <div className={styles.statusText}>Подключение к розыгрышу...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusIconError}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1" fill="currentColor"/>
            </svg>
          </div>
          <div className={styles.statusText}>{error}</div>
        </div>
      );
    }

    if (!isJoined) {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.spinner} />
          <div className={styles.statusText}>Загрузка розыгрыша...</div>
        </div>
      );
    }

    return null;
  };

  // Waiting state - raffle not started
  // Note: raffle name is already shown under the logo, so we don't duplicate it here
  const renderWaiting = () => {
    if (!raffle) return null;

    if (raffle.status === "DRAFT" || raffle.status === "READY") {
      return (
        <div className={styles.waiting}>
          {/* Animated gift icon instead of emoji */}
          <div className={styles.waitingIconAnimated}>
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.giftIcon}>
              <rect x="6" y="20" width="36" height="22" rx="4" fill="url(#gift-box-lg)" stroke="var(--raffle-primary)" strokeWidth="2"/>
              <rect x="2" y="14" width="44" height="10" rx="3" fill="url(#gift-ribbon-lg)" stroke="var(--raffle-primary)" strokeWidth="2"/>
              <path d="M24 14V42" stroke="var(--raffle-accent)" strokeWidth="3"/>
              <path d="M24 14C24 14 24 8 18 8C12 8 12 14 18 14" stroke="var(--raffle-accent)" strokeWidth="2" fill="none"/>
              <path d="M24 14C24 14 24 8 30 8C36 8 36 14 30 14" stroke="var(--raffle-accent)" strokeWidth="2" fill="none"/>
              <defs>
                <linearGradient id="gift-box-lg" x1="6" y1="20" x2="42" y2="42" gradientUnits="userSpaceOnUse">
                  <stop stopColor="rgba(79, 209, 197, 0.2)"/>
                  <stop offset="1" stopColor="rgba(102, 126, 234, 0.2)"/>
                </linearGradient>
                <linearGradient id="gift-ribbon-lg" x1="2" y1="14" x2="46" y2="24" gradientUnits="userSpaceOnUse">
                  <stop stopColor="rgba(79, 209, 197, 0.3)"/>
                  <stop offset="1" stopColor="rgba(102, 126, 234, 0.3)"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          
          {raffle.description && (
            <p className={styles.waitingDescription}>{raffle.description}</p>
          )}
          
          <div className={styles.waitingStats}>
            <div className={styles.waitingStatCard}>
              <span className={styles.waitingStatValue}>{totalPrizes}</span>
              <span className={styles.waitingStatLabel}>Призов</span>
            </div>
            <div className={styles.waitingStatCard}>
              <span className={styles.waitingStatValue}>{ticketCount}</span>
              <span className={styles.waitingStatLabel}>Участников</span>
            </div>
          </div>
          
          <div className={styles.waitingStatus}>
            {raffle.status === "READY" ? (
              <>
                <span className={styles.readyPulse} />
                <span className={styles.readyText}>Ожидание запуска...</span>
              </>
            ) : (
              <span className={styles.draftText}>Ожидание настройки...</span>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Completed state - show when admin triggers or raffle is completed
  const renderCompleted = () => {
    // Show final screen when admin triggers it OR when raffle is officially completed
    const shouldShowCompleted = showCompletedConfirmed || isCompleted;
    if (!shouldShowCompleted || showWinner) return null;

    return (
      <div className={styles.completed}>
        {/* Animated checkmark icon instead of emoji */}
        <div className={styles.completedIconAnimated}>
          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" fill="url(#completed-bg-lg)" stroke="var(--raffle-success)" strokeWidth="3"/>
            <path d="M16 24L22 30L32 18" stroke="var(--raffle-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={styles.checkPath}/>
            <defs>
              <radialGradient id="completed-bg-lg" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(24 24) rotate(90) scale(20)">
                <stop stopColor="rgba(72, 187, 120, 0.2)"/>
                <stop offset="1" stopColor="rgba(72, 187, 120, 0.05)"/>
              </radialGradient>
            </defs>
          </svg>
        </div>
        <h1 className={styles.completedTitle}>Розыгрыш завершён!</h1>

        <div className={styles.winnersGrid}>
          {completedDraws.map((draw, index) => (
            <div key={draw.prizeId} className={styles.winnerCard}>
              <div className={styles.winnerCardNumber}>{index + 1}</div>
              <div className={styles.winnerCardPrizeInfo}>
                <div className={styles.winnerCardPrize}>{draw.prizeName}</div>
                {draw.prizeDescription && (
                  <div className={styles.winnerCardPrizeDesc}>{draw.prizeDescription}</div>
                )}
              </div>
              <div className={styles.winnerCardTicket}>#{draw.ticketNumber}</div>
              <div className={styles.winnerCardName}>
                {draw.winnerName}
                {draw.winnerUsername && (
                  <span className={styles.winnerCardUsername}>
                    @{draw.winnerUsername}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Active raffle state
  // Note: title is now shown under the logo, so we use a simpler header with just stats
  const renderActive = () => {
    if (!raffle || raffle.status !== "IN_PROGRESS" || isCompleted || showCompletedConfirmed) return null;
    if (showWinner) return null;

    // During drawing: show drawingPrize
    // During transition (showingWinner): show lastDrawnPrize
    // Otherwise: show currentPrize (next prize to draw)
    const displayPrize = isDrawing ? drawingPrize : (showingWinner ? lastDrawnPrize : currentPrize);

    return (
      <div className={styles.active}>
        {/* Stats bar - positioned below logo area */}
        <div className={styles.statsBar}>
          <div className={styles.statCard}>
            <span className={styles.statCardLabel}>Призов</span>
            <span className={styles.statCardValue}>
              <span className={styles.statCardCurrent}>{drawnCount}</span>
              <span className={styles.statCardDivider}>/</span>
              <span className={styles.statCardTotal}>{totalPrizes}</span>
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statCardLabel}>Участников</span>
            <span className={styles.statCardValue}>{ticketCount}</span>
          </div>
        </div>

        {/* Raffle description */}
        {raffle.description && (
          <p className={styles.activeDescription}>{raffle.description}</p>
        )}

        {/* Status hint - above main blocks */}
        {!isDrawing && !showingWinner && !lastWinner && (
          <div className={styles.statusHint}>
            <span className={styles.hintDot} />
            Ожидание запуска розыгрыша...
          </div>
        )}

        {/* Winner display during transition (waiting for admin to press "Next") */}
        {showingWinner && lastWinner && (
          <div className={styles.lastWinnerHint}>
            <span className={styles.hintLabel}>Победитель:</span>
            <span className={styles.hintName}>{lastWinner.name}</span>
            {lastWinner.username && (
              <span className={styles.hintUsername}>@{lastWinner.username}</span>
            )}
            <span className={styles.hintTicket}>#{lastWinner.ticketNumber}</span>
          </div>
        )}

        {/* Main content - prize and roller blocks with equal size */}
        <div className={`${styles.main} ${isTransitioning ? styles.transitioning : ""}`}>
          {/* Prize display */}
          <div className={`${styles.prizeContainer} ${isTransitioning ? styles.transitionOut : ""}`}>
            <PrizeDisplay
              prize={displayPrize}
              isDrawing={isDrawing}
              size="large"
            />
          </div>

          {/* Ticket roller */}
          <div className={`${styles.rollerContainer} ${isTransitioning ? styles.transitionOut : ""}`}>
            <TicketRoller
              currentNumber={currentTicketNumber}
              ticketNumbers={ticketNumbers}
              isSpinning={isDrawing}
              progress={drawProgress}
              winningNumber={lastWinner?.ticketNumber}
              size="large"
              onAnimationComplete={confirmAnimationComplete}
            />
          </div>
        </div>

        {/* Completed draws list */}
        {completedDraws.length > 0 && (
          <div className={styles.completedList}>
            <div className={styles.completedListHeader}>
              <span className={styles.completedListTitle}>Разыграно</span>
              <span className={styles.completedListCount}>{completedDraws.length}</span>
            </div>
            <div className={styles.completedListItems}>
              {completedDraws.map((draw) => (
                <div key={draw.prizeId} className={styles.completedListItem}>
                  <span className={styles.completedListPrize}>
                    {draw.prizeName}
                  </span>
                  <span className={styles.completedListArrow}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className={styles.completedListTicket}>
                    #{draw.ticketNumber}
                  </span>
                  <span className={styles.completedListWinner}>
                    {draw.winnerName}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={styles.container}>
      {/* Logo with rays - ref used for position calculation */}
      <div className={styles.logoHeader}>
        <RaffleLogo ref={logoRef} size="large" onLoad={updateRaysPosition} />
        {raffle?.name && (
          <div className={styles.raffleNameContainer}>
            <h1 className={styles.raffleName}>{raffle.name}</h1>
            {raffle.isTest && (
              <span className={styles.testBadge}>Тестовый режим</span>
            )}
          </div>
        )}
      </div>

      {/* Status overlay */}
      {renderStatus()}

      {/* Waiting state */}
      {renderWaiting()}

      {/* Active state */}
      {renderActive()}

      {/* Completed state */}
      {renderCompleted()}

      {/* Winner announcement overlay */}
      <WinnerAnnouncement
        winner={lastWinner}
        prize={drawingPrize}
        isVisible={showWinner}
        size="large"
      />

      {/* Confetti on winner */}
      <Confetti active={showWinner} count={100} duration={6} />

      {/* Reaction bursts */}
      <ReactionBurst
        reactions={reactionBurst}
        trigger={reactionTrigger}
        position="sides"
        size="large"
      />
    </div>
  );
}

export default RaffleScreen;
